import Table from '@components/new/Table/Table';
import { iTableConfigItem } from '@components/new/Table/Table.helpers';
import { OnSiteSegmentGroup } from '@apiHooks/useLandscapeRestaurantData';
import {
    ON_SITE_TABLE_TITLE,
    ON_SITE_SEGMENT_ORDER,
    ON_SITE_LEFT_COLUMN_COUNT,
    OnSiteSegment,
} from './LandscapeOnSiteTable.constants';

type FlatRow = { kind: 'parent' | 'child'; name: string; units: number };

function flattenGroups(groups: OnSiteSegmentGroup[]): FlatRow[] {
    const rows: FlatRow[] = [];
    groups.forEach((group) => {
        rows.push({ kind: 'parent', name: group.parentSegment ?? '', units: group.parentSegmentUnits ?? 0 });
        (group.segments ?? []).forEach((seg) => {
            rows.push({ kind: 'child', name: seg.segmentName ?? '', units: seg.units ?? 0 });
        });
    });
    return rows;
}

function makeConfig(flatRows: FlatRow[]): iTableConfigItem[] {
    return [
        {
            title: '',
            styles: { cell: { paddingLeft: 0 } },
            Value: ({ index }) => {
                const row = flatRows[index];
                if (!row) return null;
                const labelClass = row.kind === 'child'
                    ? 'LandscapeOnSiteTable__segment-label LandscapeOnSiteTable__segment-label--child'
                    : 'LandscapeOnSiteTable__segment-label';
                return <span className={labelClass}>{row.name}</span>;
            },
        },
        {
            title: 'Units',
            width: '100px',
            textCentered: true,
            Value: ({ index }) => {
                const row = flatRows[index];
                if (!row) return null;
                return <span className="LandscapeOnSiteTable__unit-value">{row.units.toLocaleString()}</span>;
            },
        },
    ];
}

interface Props {
    data: OnSiteSegmentGroup[];
}

const LandscapeOnSiteTable = ({ data }: Props) => {
    if (data.length === 0) {
        return (
            <div className="LandscapeOnSiteTable" data-testid="blockLandscapeOnSiteTable">
                <div className="LandscapeOnSiteTable__empty">No data available</div>
            </div>
        );
    }

    const sorted = [...data].sort((a, b) => {
        const ai = ON_SITE_SEGMENT_ORDER.indexOf(a.parentSegment as OnSiteSegment);
        const bi = ON_SITE_SEGMENT_ORDER.indexOf(b.parentSegment as OnSiteSegment);
        const aIdx = ai === -1 ? ON_SITE_SEGMENT_ORDER.length : ai;
        const bIdx = bi === -1 ? ON_SITE_SEGMENT_ORDER.length : bi;
        return aIdx - bIdx;
    });
    const columns = [sorted.slice(0, ON_SITE_LEFT_COLUMN_COUNT), sorted.slice(ON_SITE_LEFT_COLUMN_COUNT)];

    return (
        <div className="LandscapeOnSiteTable" data-testid="blockLandscapeOnSiteTable">
            <h3 className="LandscapeSegmentsTable__title">{ON_SITE_TABLE_TITLE}</h3>
            <div className="LandscapeOnSiteTable__columns">
                {columns.map((columnGroups, colIdx) => {
                    const flatRows = flattenGroups(columnGroups);
                    const config = makeConfig(flatRows);
                    return (
                        <div key={columnGroups[0]?.parentSegment ?? colIdx} className="LandscapeOnSiteTable__column">
                            <Table
                                config={config}
                                rowsCount={flatRows.length}
                                hasInnerScroll={false}
                                isCopyButtonHidden
                                getRowProps={(index) => {
                                    const row = flatRows[index];
                                    return row?.kind === 'parent'
                                        ? { className: 'TableNew__row TableNew__row--parent' }
                                        : {};
                                }}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default LandscapeOnSiteTable;
